import * as React from 'react';
import RecordType from '../data/RecordType';
import { Overlay } from '../components/Overlay';
import FileType from '../data/FileType';
import { download } from '../utils/Download';
import {
  loadFile,
  canCompressGIFsLocally,
  isMobileLayout,
  isIOS
} from '../utils';
import WrapperInterface from '../utils/WrapperInterface';
import {
  compressGIFCloud,
  increaseTemplateUsage,
  incrementMonthlyDownloadedFlyers
} from '../utils/FirebaseUtils';
import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import { trackEvent } from '../utils/Tracker';
import { getExportedFileName } from 'state/FlyerState';
import { PlaybackSmoothness } from 'data/PlaybackSmoothness';
import { PlatformType } from 'data/PlatformType';
import WarningMessage from 'showcase/components/WarningMessage';
import { isFigmaPro } from 'state/AppState';
import { Modal } from 'components/Modal';
import { SignIn } from 'signin/SignIn';

const SlowMessage = () => {
  const { platform } = getStoreState().app;
  let message: React.ReactNode;
  if (platform === PlatformType.iOSApp) {
    message = <>please enable "Don't skip frames" when exporting.</>;
  } else if (isMobileLayout()) {
    if (isIOS()) {
      // suggest app
      message = (
        <>
          please use the{' '}
          <a
            href="https://apps.apple.com/app/beatflyer-animated-flyers/id6444857001"
            target="_blank"
            rel="noopener noreferrer"
          >
            mobile app
          </a>
          .
        </>
      );
    } else {
      // suggest to use the PC
      message = <>please use your desktop computer.</>;
    }
  } else {
    // suggest to use Chrome
    message = <>please use Google Chrome.</>;
  }
  return (
    <div style={{ marginBottom: 16 }}>
      <WarningMessage>
        Your device could not record this video smoothly. To record graphical
        intensive animations smoothly, {message}
      </WarningMessage>
    </div>
  );
};

interface Props {
  recordType: RecordType;
  recordingUrl: string;
  recordingSmoothness: PlaybackSmoothness;
}

interface State {
  isCompressing: boolean;
  showSignIn: boolean;
}

const compressGIF = async (blob: Blob): Promise<Blob | null> => {
  if (canCompressGIFsLocally()) {
    const wi: WrapperInterface = (window as any).beatflyerWrapperInterface;
    return await wi.compressGIF(blob);
  } else {
    return await compressGIFCloud(blob);
  }
};

export class RecordingReviewModal extends React.Component<Props, State> {
  recordingCompressedUrl: string | undefined;
  videoEl = React.createRef<HTMLVideoElement>();
  state: State = {
    isCompressing: false,
    showSignIn: false
  };

  componentWillUnmount() {
    if (this.recordingCompressedUrl) {
      URL.revokeObjectURL(this.recordingCompressedUrl);
    }
  }

  startDownload = async () => {
    const { app } = getStoreState();
    const { isBeatflyerLite } = app;
    const { recordType, recordingUrl } = this.props;
    trackEvent('Download media', { recordType });
    if (recordType === RecordType.GIF) {
      const shouldCompress = !isBeatflyerLite || isFigmaPro(app);
      if (shouldCompress && !this.recordingCompressedUrl) {
        this.setState({ isCompressing: true });
        trackEvent('Compress GIF');
        const originalBlob = await loadFile(recordingUrl);
        let blob = await compressGIF(originalBlob);
        this.setState({ isCompressing: false });
        if (!blob) {
          alert(
            `Something went wrong and we couldn't reduce the GIF file size, it will likely be too large to share it. If you need a smaller file, please try again.`
          );
          blob = originalBlob;
        }
        this.recordingCompressedUrl = URL.createObjectURL(blob);
      }
    }
    const templateId = getStoreState().flyer.originTemplateId ?? undefined;
    trackEvent('Download video', {
      templateId
    });
    const ext = recordType === RecordType.GIF ? 'gif' : 'mp4';
    const mimetype = recordType === RecordType.GIF ? 'image/gif' : 'video/mp4';
    const flyerState = getStoreState().flyer;
    const filename = `${getExportedFileName(flyerState)}.${ext}`;
    download(this.recordingCompressedUrl || recordingUrl, filename, mimetype);
    await incrementMonthlyDownloadedFlyers(getStoreState().app.user?.uid);
    if (templateId !== undefined) {
      await increaseTemplateUsage(templateId);
    }
  };

  shareVideo = () => {
    this.videoEl.current?.pause();
    const { user } = getStoreState().app;
    if (!user) {
      this.setState({ showSignIn: true });
      return;
    }
    storeDispatch(Actions.shareVideo(this.props.recordingUrl));
  };

  buyFigma = () => {
    storeDispatch(Actions.showBuyFigma('gif preview'));
  };

  render() {
    const { recordType, recordingUrl, recordingSmoothness } = this.props;
    const { app } = getStoreState();
    const { isBeatflyerLite, platform } = app;
    let recordingElement: React.ReactNode;
    switch (recordType) {
      case RecordType.VideoAsync:
      case RecordType.VideoLive:
        recordingElement = (
          <video
            autoPlay={true}
            controls={true}
            loop={true}
            ref={this.videoEl}
            playsInline={true}
          >
            <source src={recordingUrl} type={FileType.mp4} />
          </video>
        );
        break;
      case RecordType.GIF:
        recordingElement = (
          <img className="recorded-gif" src={recordingUrl} draggable={false} />
        );
        break;
      default:
        recordingElement = <div />;
        break;
    }

    const buttons: JSX.Element[] = [];
    if (
      recordType !== RecordType.GIF &&
      !isBeatflyerLite &&
      !isMobileLayout()
    ) {
      buttons.push(
        <button className="large secondary" key="0" onClick={this.shareVideo}>
          Share with mobile
        </button>
      );
    }
    buttons.push(
      <button
        className="large primary"
        key="1"
        onClick={this.startDownload}
        disabled={this.state.isCompressing}
      >
        {this.state.isCompressing ? 'Compressing...' : 'Download'}
      </button>
    );

    return (
      <>
        <Overlay
          title={`Your ${
            recordType === RecordType.GIF ? 'GIF' : 'video'
          } is ready`}
          fullWidth={true}
        >
          <div className="recording-preview">{recordingElement}</div>
          {recordingSmoothness === PlaybackSmoothness.Slow && <SlowMessage />}
          {platform === PlatformType.Figma &&
            !isFigmaPro(app) &&
            recordType === RecordType.GIF && (
              <div style={{ marginBottom: 16, fontSize: 16 }}>
                <a onClick={this.buyFigma}>Upgrade</a> to reduce substantially
                animated GIFs file size
              </div>
            )}
          <div className="recording-buttons">{buttons}</div>
        </Overlay>
        {this.state.showSignIn && (
          <Modal>
            <SignIn
              onClose={success => {
                if (success) {
                  this.shareVideo();
                }
                this.setState({ showSignIn: false });
              }}
            />
          </Modal>
        )}
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/video/RecordingReviewModal.tsx